import {
    createSignatureBorderStyle,
    createSignatureBorderStyleButtons
} from "@/app/theme/theme-extensions";

// theme-tokens.ts
import { alpha } from "@mui/material/styles";

const toRem = (px: number) => `${px / 16}rem`;

const defaultFontFamily = `Roboto Flex, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"`;
const headingWrapStyle = {
    wordBreak: "normal",
    overflowWrap: "normal",
    hyphens: "auto",
    WebkitHyphens: "auto",
};

export const signature = {
    main: "#DA9C23", // Signature brand color
    // Tonal ramp (tuned to keep AA in both light/dark)
    50:  "#FFF8E9",
    100: "#FDEBC7",
    200: "#F8D886",
    300: "#F1C756",
    400: "#E7B537",
    500: "#DA9C23", // main
    600: "#B67E1C",
    700: "#936216",
    800: "#734B10",
    900: "#57380C",
};

export const neutral = {
    // Balanced grays tuned for readability (not pure grayscale to avoid banding)
    0:   "#FFFFFF",
    50:  "#FAFAFA",
    100: "#F2F2F2",
    200: "#E6E6E6",
    300: "#D1D1D1",
    400: "#B0B0B0",
    500: "#8C8C8C",
    600: "#6E6E6E",
    700: "#515151",
    800: "#353535",
    900: "#1C1C1C",
    1000:"#000000",
};

export const support = {
    success: { main: "#2E7D32", light: "#4CAF50", dark: "#1B5E20", contrastText: neutral[0] },
    warning: { main: "#ED6C02", light: "#FF9800", dark: "#E65100", contrastText: neutral[1000] },
    error:   { main: "#C62828", light: "#EF5350", dark: "#8E0000", contrastText: neutral[0] },
    info:    { main: "#0277BD", light: "#03A9F4", dark: "#01579B", contrastText: neutral[0] },
    breakout:{ main: "#a72e6b", light: "#d5669e", dark: "#571838", contrastText: neutral[0] },
};

export const focusRing = (bg: string) => ({
    outline: `2px solid ${alpha(signature[600], 0.9)}`,
    outlineOffset: 2,
    backgroundColor: bg
});

export const shadows = {
    // Subtle, consistent shadows for light/dark
    level1: (c: string) => `0 1px 3px ${alpha(c, 0.12)}, 0 1px 2px ${alpha(c, 0.24)}`,
    level2: (c: string) => `0 3px 6px ${alpha(c, 0.16)}, 0 3px 6px ${alpha(c, 0.23)}`,
};

export const radius = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    pill: 999,
};

export const typography = {
    fontFamily: defaultFontFamily,
    h1: { fontSize: toRem(40), fontWeight: 700, lineHeight: 1.2, ...headingWrapStyle },
    h2: { fontSize: toRem(32), fontWeight: 700, lineHeight: 1.25, ...headingWrapStyle },
    h3: { fontSize: toRem(30), fontWeight: 700, lineHeight: 1.25, ...headingWrapStyle },
    h4: { fontSize: toRem(28), fontWeight: 600, lineHeight: 1.3, ...headingWrapStyle },
    h5: { fontSize: toRem(26), fontWeight: 600, lineHeight: 1.35, ...headingWrapStyle },
    h6: { fontSize: toRem(24), fontWeight: 600, lineHeight: 1.4, ...headingWrapStyle },
    body1: { fontSize: toRem(18), fontWeight: 600, lineHeight: 1.6 },
    body2: { fontSize: toRem(20), fontWeight: 800, lineHeight: 1.7 },
    subtitle1: { fontSize: toRem(18), fontWeight: 600, lineHeight: 1.6, textTransform: 'uppercase' },
    subtitle2: { fontSize: toRem(16), fontWeight: 980, lineHeight: 1.6, letterSpacing: '0.05rem', fontVariationSettings: "'wght' 980" },
    button: { textTransform: "none", fontWeight: 600, fontSize: toRem(18) },
    caption: { fontSize: toRem(16), fontWeight: 600, lineHeight: 1.4, fontStyle: 'italic', letterSpacing: '0.05rem'},
    overline: { fontSize: toRem(14), fontWeight: 900, lineHeight: 1.4, fontStyle: 'italic', letterSpacing: '0.05rem', color: neutral[500] },
};

export const signatureBorderStyle = createSignatureBorderStyle();
export const signatureBorderStyleButtons = createSignatureBorderStyleButtons();
